import React, { useState } from "react";
import styles from "./OrderLookup.module.css";
import { getOrderByGeneratedId } from "../../lib/getOrderByGeneratedId";
import ProductImage from "../../components/common/ProductImage/ProductImage";

interface Product {
  id: number;
  quantity: number;
  price: number;
  finalPrice: number;
  product: {
    id: number;
    name: string;
    images: {
      thumbnail: string;
      small: string;
      medium: string | null;
    }[];
  };
}

interface Group {
  id: number;
  deliveryMethod: string;
  deliveryDate: string; // Add this
  subTotal: number; // Add this
  deliveryFee: number; // Add this
  groupTotal: number;
  products: Product[];
}

interface OrderData {
  orderId: string;
  createdAt: string;
  orderDate: string;
  generatedOrderId: string;
  customerName: string;
  totalPrice: number;
  totalDeliveryFee: number;
  finalTotal: number;
  paymentMethod: string;
  groups: Group[];
  orderStatus: "processing" | "shipped" | "delivered";
  paymentStatus: "pending" | "paid";
}

const steps = [
  "Pendiente de Pago",
  "Confirmado",
  "Preparando",
  "En camino",
  "Entregado",
];

const OrderLookup: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [generatedOrderId, setGeneratedOrderId] = useState<string>("");
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    console.log("email", email);
    console.log("generatedOrderId", generatedOrderId);

    try {
      const response = await getOrderByGeneratedId(email, generatedOrderId);
      if (response?.orderData) {
        console.log("response", JSON.stringify(response));
        setOrderData(response.orderData);
      } else {
        setError(response?.message || "Order not found.");
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getActiveStepIndex = (order: OrderData) => {
    if (order.paymentStatus === "pending") return 0;
    if (order.paymentStatus === "paid") return 1;
    if (order.orderStatus === "shipped") return 3;
    if (order.orderStatus === "delivered") return 4;
    return 2; // "Preparando" is default after paid
  };

  const renderStepper = (order: OrderData) => {
    const activeStepIndex = getActiveStepIndex(order);

    return (
      <div className={styles.stepper}>
        {steps.map((step, index) => {
          const isCompleted = index < activeStepIndex;
          const isActive = index === activeStepIndex;

          return (
            <div key={index} className={styles.step}>
              <div
                className={`${styles.circle} ${
                  isCompleted ? styles.completed : ""
                } ${isActive ? styles.active : ""}`}
              >
                {index + 1}
              </div>
              <div className={styles.stepLabel}>{step}</div>
              {index !== steps.length - 1 && (
                <div
                  className={`${styles.connector} ${
                    isCompleted ? styles.completedConnector : ""
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const formatDeliveryMethod = (method: string) => {
    switch (method.toLowerCase()) {
      case "standard":
        return "Standard";
      case "rapido":
        return "Rápido";
      case "express":
        return "Express";
      default:
        return method;
    }
  };

  const formatDate = (date: string) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    };
    return new Date(date).toLocaleDateString("es-ES", options);
  };

  const renderGroupProduct = (product: Product) => (
    <div key={product.id} className={styles.product}>
      <ProductImage
        src={`${process.env.REACT_APP_IMAGE_FOLDER}${product.product.images[0]?.thumbnail}`}
        alt={product.product.name}
        className={styles.productImage}
      />
      <div className={styles.productInfo}>
        <span className={styles.productName}>{product.product.name}</span>
        <div className={styles.productPricing}>
          <span className={styles.productDetails}>
            {product.quantity} x S/ {product.price.toFixed(2)}
          </span>
          <span className={styles.productTotal}>
            S/ {product.finalPrice.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );

  const renderGroup = (group: Group, index: number) => (
    <div key={group.id} className={styles.cartGroup}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h2>{`Paquete ${index + 1} - ${formatDeliveryMethod(
            group.deliveryMethod
          )} - ${formatDate(group.deliveryDate)}`}</h2>
        </div>
      </div>

      <div className={styles.items}>
        {group.products.map(renderGroupProduct)}
      </div>

      <div className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.row}>
            <span>Subtotal:</span>
            <span>S/ {group.subTotal.toFixed(2)}</span>
          </div>
          <div className={styles.row}>
            <span>Costo de Envío:</span>
            <span>S/ {group.deliveryFee.toFixed(2)}</span>
          </div>
          <div className={`${styles.row} ${styles.total}`}>
            <span>Total:</span>
            <span>S/ {group.groupTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Sigue tu pedido</h1>

      <div className={styles.searchSection}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inputGroup}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Ingresa tu correo electrónico"
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="generatedOrderId">Número de Orden</label>
            <input
              type="text"
              id="generatedOrderId"
              value={generatedOrderId}
              onChange={(e) => setGeneratedOrderId(e.target.value)}
              required
              placeholder="Ingresa el número de orden"
            />
          </div>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? "Buscando..." : "Buscar"}
          </button>
        </form>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {orderData && (
        <>
          {renderStepper(orderData)}

          <div className={styles.orderInfo}>
            <div className={styles.card}>
              <h2>Información de la Orden</h2>
              <div className={styles.infoGrid}>
                <p>
                  <strong>Orden #{orderData.generatedOrderId}</strong>
                </p>
                <p>
                  <strong>Cliente:</strong> {orderData.customerName}
                </p>
                <p>
                  <strong>Método de pago:</strong>{" "}
                  {orderData.paymentMethod === "money_transfer"
                    ? "Transferencia Bancaria"
                    : orderData.paymentMethod === "yapePlin"
                    ? "Yape / Plin"
                    : orderData.paymentMethod}
                </p>
                <p>
                  <span
                    className={`${styles.statusBadge} ${
                      orderData.paymentStatus === "paid"
                        ? styles.statusPaid
                        : styles.statusPending
                    }`}
                  >
                    {orderData.paymentStatus === "paid"
                      ? "Pagado"
                      : "Pendiente"}
                  </span>
                </p>
              </div>
            </div>

            <div className={styles.orderSummary}>
              <h2>Detalles de la Orden</h2>
              <div className={styles.groupsContainer}>
                {orderData.groups.map((group, index) =>
                  renderGroup(group, index)
                )}
              </div>
              <div className={styles.footer}>
                <div className={styles.footerContent}>
                  <div className={styles.row}>
                    <span>Subtotal:</span>
                    <span>S/ {orderData.totalPrice.toFixed(2)}</span>
                  </div>
                  <div className={styles.row}>
                    <span>Total Envío:</span>
                    <span>S/ {orderData.totalDeliveryFee.toFixed(2)}</span>
                  </div>
                  <div className={`${styles.row} ${styles.total}`}>
                    <span>Total de la Orden:</span>
                    <span>S/ {orderData.finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderLookup;
